<template>
  <div>
    <div class="modal show fade modal text-left" style="display: block;" v-if="show">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h4 class="modal-title" style="text-transform:capitalize;">
              Referral Codes
              <small class="text-muted" v-if="partner">
                - {{ partner.first_name }} {{ partner.last_name }} ({{ partner.email }})
              </small>
            </h4>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="$emit('hide')">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">

            <!-- create / update referral code form -->
            <div class="card border mb-2" v-if="showCreateForm">
              <div class="card-header">
                <h5 class="mb-0">
                  {{ editingCode ? 'Update Referral Code' : 'Create Referral Code' }}
                  <span v-if="editingCode" class="badge badge-light-primary ml-50">{{ editingCode.code }}</span>
                </h5>
              </div>
              <div class="card-body">
                <form class="validate-form" @submit.prevent="submitForm">
                  <div class="row">
                    <div class="col-md-3" v-for="field in rateFields" :key="field.name">
                      <div class="form-group">
                        <label :for="'referral-' + field.name">
                          {{ field.label }}
                          <span class="text-danger" v-if="!editingCode">*</span>
                          <small class="text-muted" v-else>(optional)</small>
                        </label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          max="100"
                          class="form-control"
                          :id="'referral-' + field.name"
                          v-model="formData[field.name]"
                          v-validate="rateRules"
                          :name="field.name"
                          :data-vv-as="field.label.toLowerCase()"
                          :class="errorBag.has(field.name) ? 'is-invalid' : ''"
                          :placeholder="field.placeholder"
                        />
                        <small v-show="errorBag.has(field.name)" class="invalid-feedback">
                          {{ errorBag.first(field.name) }}
                        </small>
                      </div>
                    </div>
                    <div class="col-md-3" v-if="editingCode">
                      <div class="form-group">
                        <label for="referralStatus">Status <span class="text-danger">*</span></label>
                        <select
                          class="form-control"
                          id="referralStatus"
                          v-model="formData.status"
                          v-validate="'required'"
                          name="status"
                          :class="errorBag.has('status') ? 'is-invalid' : ''"
                        >
                          <option value="1">Active</option>
                          <option value="0">Inactive</option>
                        </select>
                        <small v-show="errorBag.has('status')" class="invalid-feedback">
                          {{ errorBag.first('status') }}
                        </small>
                      </div>
                    </div>
                    <div class="col-md-3" v-if="!editingCode">
                      <div class="form-group">
                        <label for="referralCode">Referral Code <small class="text-muted">(optional)</small></label>
                        <input
                          type="text"
                          class="form-control"
                          id="referralCode"
                          v-model="formData.referralCode"
                          v-validate="'alpha_num|max:20'"
                          name="referralCode"
                          data-vv-as="referral code"
                          :class="errorBag.has('referralCode') ? 'is-invalid' : ''"
                          placeholder="Leave empty to auto-generate"
                        />
                        <small v-show="errorBag.has('referralCode')" class="invalid-feedback">
                          {{ errorBag.first('referralCode') }}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex justify-content-end">
                    <button type="button" class="btn btn-secondary mr-1" @click="hideCreateForm" :disabled="creating">
                      Cancel
                    </button>
                    <button type="submit" class="btn btn-primary d-flex" :disabled="creating">
                      <span v-if="creating" class="spinner-border spinner-border-sm mr-1"></span>
                      {{ submitButtonText }}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <!-- referral codes listing -->
            <div class="d-flex justify-content-between align-items-center mb-1">
              <p class="mb-0 text-primary">
                <strong>Total Codes : </strong><span class="text-success">{{ codes.length }}</span>
              </p>
              <button type="button" class="btn btn-sm btn-primary" v-if="!showCreateForm" @click="openCreateForm">
                Create Referral Code
              </button>
            </div>
            <div class="table-responsive">
              <table class="table table-hover">
                <thead class="thead-light">
                  <tr>
                    <th>S.No</th>
                    <th>Code</th>
                    <th>Charges</th>
                    <th>Created Date / Time</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!codes.length">
                    <td colspan="6" class="font-weight-bolder font-medium-2 text-center">
                      No referral codes available
                    </td>
                  </tr>
                  <template v-for="(code, index) in codes">
                  <tr :key="code.id">
                    <td>{{ index + 1 }}</td>
                    <td><span class="text-primary font-weight-bolder">{{ code.code || '-' }}</span></td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-sm d-flex align-items-center"
                        :class="expandedCodeId == code.id ? 'btn-outline-danger' : 'btn-outline-danger'"
                        @click="toggleCharges(code)"
                      >
                        {{ expandedCodeId == code.id ? 'Hide Charges' : 'View Charges' }}
                        <span class="badge badge-light-primary ml-50">{{ chargesOf(code).length }}</span>
                      </button>
                    </td>
                    <td>{{ customFormatDateTime(code.created_at) }}</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="custom-control custom-switch custom-switch-success">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            :id="'referral-code-status-' + code.id"
                            :checked="code.status == 1"
                            :disabled="updatingId == code.id"
                            @change="toggleStatus(code, $event)"
                          />
                          <label class="custom-control-label" :for="'referral-code-status-' + code.id"></label>
                        </div>
                        <span v-if="updatingId == code.id" class="spinner-border spinner-border-sm text-primary ml-50"></span>
                        <span v-else class="badge badge-sm ml-50" :class="code.status == 1 ? 'badge-light-success' : 'badge-light-danger'">
                          {{ code.status == 1 ? 'Active' : 'Inactive' }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="creating || updatingId == code.id"
                        @click="openEditForm(code)"
                      >
                        Update
                      </button>
                    </td>
                  </tr>
                  <tr v-if="expandedCodeId == code.id" :key="code.id + '-charges'" class="charges-row">
                    <td colspan="6" class="p-1">
                      <div class="card border mb-0">
                        <div class="card-header py-50">
                          <h6 class="mb-0">Charges for <span class="text-primary">{{ code.code }}</span></h6>
                        </div>
                        <div class="table-responsive">
                          <table class="table table-sm mb-0">
                            <thead class="thead-light">
                              <tr>
                                <th>Type</th>
                                <th>Buying Rate</th>
                                <th>Selling Rate</th>
                                <th>Commission Rate</th>
                                <th>Status</th>
                                <th>Created Date / Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-if="!chargesOf(code).length">
                                <td colspan="6" class="text-center font-weight-bolder">No charges available</td>
                              </tr>
                              <tr v-for="charge in chargesOf(code)" :key="charge.id">
                                <td>
                                  <span class="badge" :class="charge.charges_type == 1 ? 'badge-light-info' : 'badge-light-primary'">
                                    {{ chargeTypeLabel(charge.charges_type) }}
                                  </span>
                                </td>
                                <td>{{ formatRate(charge.buying_rate) }}</td>
                                <td>{{ formatRate(charge.selling_rate) }}</td>
                                <td>{{ formatRate(charge.commission_rate) }}</td>
                                <td>
                                  <span class="badge badge-sm" :class="charge.status == 1 ? 'badge-light-success' : 'badge-light-danger'">
                                    {{ charge.status == 1 ? 'Active' : 'Inactive' }}
                                  </span>
                                </td>
                                <td>{{ charge.created_at ? customFormatDateTime(charge.created_at) : '-' }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer justify-content-end">
            <button type="button" class="btn btn-secondary" @click="$emit('hide')">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="show" class="modal-backdrop fade show"></div>
  </div>
</template>

<script>
// charges_type on the charges object: 0 = domestic, 1 = international
const CHARGE_TYPE_DOMESTIC = 0
const CHARGE_TYPE_INTERNATIONAL = 1

const RATE_FIELDS = [
  { name: 'domesticBuyingRate', chargeType: CHARGE_TYPE_DOMESTIC, chargeKey: 'buying_rate', label: 'Domestic Buying Rate', placeholder: 'e.g. 1.5' },
  { name: 'domesticSellingRate', chargeType: CHARGE_TYPE_DOMESTIC, chargeKey: 'selling_rate', label: 'Domestic Selling Rate', placeholder: 'e.g. 1.7' },
  { name: 'internationalBuyingRate', chargeType: CHARGE_TYPE_INTERNATIONAL, chargeKey: 'buying_rate', label: 'International Buying Rate', placeholder: 'e.g. 2.5' },
  { name: 'internationalSellingRate', chargeType: CHARGE_TYPE_INTERNATIONAL, chargeKey: 'selling_rate', label: 'International Selling Rate', placeholder: 'e.g. 2.7' }
]

export default {
  props: ['show', 'partner'],
  data() {
    return {
      rateFields: RATE_FIELDS,
      codes: [],
      showCreateForm: false,
      editingCode: null,
      creating: false,
      updatingId: null,
      expandedCodeId: null,
      formData: this.emptyForm()
    };
  },
  computed: {
    submitButtonText() {
      if (this.editingCode) {
        return this.creating ? 'Updating...' : 'Update'
      }
      return this.creating ? 'Creating...' : 'Create'
    },
    rateRules() {
      // rates are required on create, optional on update, always capped at 100
      return this.editingCode
        ? 'decimal|min_value:0|max_value:100'
        : 'required|decimal|min_value:0|max_value:100'
    }
  },
  methods: {
    emptyForm() {
      let form = { referralCode: '', status: '1' }
      RATE_FIELDS.forEach(field => {
        form[field.name] = ''
      })
      return form
    },
    loadCodes() {
      let list = this.partner && Array.isArray(this.partner.referral_codes) ? this.partner.referral_codes : []
      this.codes = list.map(code => this.cloneCode(code))
      this.expandedCodeId = null
    },
    cloneCode(code) {
      let copy = {...code}
      copy.charges = Array.isArray(code.charges) ? code.charges.map(charge => ({...charge})) : []
      return copy
    },
    chargesOf(code) {
      return Array.isArray(code.charges) ? code.charges : []
    },
    chargeOfType(code, chargeType) {
      return this.chargesOf(code).find(charge => charge.charges_type == chargeType) || null
    },
    chargeTypeLabel(chargeType) {
      if (chargeType == CHARGE_TYPE_INTERNATIONAL) {
        return 'International'
      }
      if (chargeType == CHARGE_TYPE_DOMESTIC) {
        return 'Domestic'
      }
      return 'Unknown'
    },
    toggleCharges(code) {
      this.expandedCodeId = this.expandedCodeId == code.id ? null : code.id
    },
    formatRate(value) {
      if (value === null || typeof (value) == 'undefined' || value === '') {
        return '-'
      }
      return value
    },
    hasValue(value) {
      return value !== null && typeof (value) != 'undefined' && value !== ''
    },
    toggleStatus(code, event) {
      let newStatus = event.target.checked ? 1 : 0
      let previousStatus = code.status
      this.updatingId = code.id

      let requestData = {
        referralCodeId: code.id,
        status: newStatus
      }

      this.makeRequest('post', 'v1/update-referral-code', requestData).then(callResponse => {
        this.updatingId = null
        if (!callResponse.error && callResponse.response.data.code == 200) {
          code.status = newStatus
          this.syncCodeToPartner(code)
          this.$emit('updated')
        } else {
          // revert the switch to its previous state
          code.status = previousStatus
          event.target.checked = previousStatus == 1
        }
      });
    },
    openCreateForm() {
      this.editingCode = null
      this.resetForm()
      this.showCreateForm = true
    },
    openEditForm(code) {
      this.editingCode = code
      let form = this.emptyForm()
      RATE_FIELDS.forEach(field => {
        let charge = this.chargeOfType(code, field.chargeType)
        form[field.name] = charge && this.hasValue(charge[field.chargeKey]) ? charge[field.chargeKey] : ''
      })
      form.status = String(code.status == 1 ? 1 : 0)
      this.formData = form
      this.$nextTick(() => {
        this.$validator.reset()
      })
      this.showCreateForm = true
    },
    submitForm() {
      if (this.editingCode) {
        this.updateReferralCode()
      } else {
        this.createReferralCode()
      }
    },
    createReferralCode() {
      this.$validator.validateAll().then(valid => {
        if (!valid) {
          return
        }
        this.creating = true

        let requestData = {
          partnerId: this.partner.id
        }
        RATE_FIELDS.forEach(field => {
          requestData[field.name] = this.formData[field.name]
        })
        if (this.formData.referralCode) {
          requestData.referralCode = this.formData.referralCode
        }

        this.makeRequest('post', 'v1/create-referral-code', requestData).then(callResponse => {
          this.creating = false
          if (!callResponse.error && callResponse.response.data.code == 200) {
            let created = callResponse.response.data.data
            if (created && typeof (created.code) != 'undefined') {
              let newCode = this.cloneCode(created)
              this.codes.unshift(newCode)
              this.syncCodeToPartner(newCode)
            }
            this.hideCreateForm()
            this.$emit('updated')
          }
        });
      });
    },
    updateReferralCode() {
      this.$validator.validateAll().then(valid => {
        if (!valid) {
          return
        }
        this.creating = true
        let code = this.editingCode
        let newStatus = parseInt(this.formData.status)

        let requestData = {
          referralCodeId: code.id,
          status: newStatus
        }
        // rates are optional on update, only send the ones that were filled in
        RATE_FIELDS.forEach(field => {
          if (this.hasValue(this.formData[field.name])) {
            requestData[field.name] = this.formData[field.name]
          }
        })

        this.makeRequest('post', 'v1/update-referral-code', requestData).then(callResponse => {
          this.creating = false
          if (!callResponse.error && callResponse.response.data.code == 200) {
            code.status = newStatus
            let updated = callResponse.response.data.data
            if (updated && typeof (updated.code) != 'undefined') {
              Object.assign(code, this.cloneCode(updated))
            } else {
              RATE_FIELDS.forEach(field => {
                if (this.hasValue(this.formData[field.name])) {
                  let charge = this.chargeOfType(code, field.chargeType)
                  if (charge) {
                    this.$set(charge, field.chargeKey, this.formData[field.name])
                  }
                }
              })
            }
            this.syncCodeToPartner(code)
            this.hideCreateForm()
            this.$emit('updated')
          }
        });
      });
    },
    syncCodeToPartner(code) {
      // keep the row object from the listing in sync so the table reflects the change without a reload
      if (!this.partner) {
        return
      }
      if (!Array.isArray(this.partner.referral_codes)) {
        this.$set(this.partner, 'referral_codes', [])
      }
      let existingIndex = this.partner.referral_codes.findIndex(item => item.id == code.id)
      if (existingIndex > -1) {
        this.$set(this.partner.referral_codes, existingIndex, this.cloneCode(code))
      } else {
        this.partner.referral_codes.unshift(this.cloneCode(code))
      }
    },
    hideCreateForm() {
      this.showCreateForm = false
      this.editingCode = null
      this.resetForm()
    },
    resetForm() {
      this.formData = this.emptyForm()
      this.$nextTick(() => {
        this.$validator.reset()
      })
    }
  },
  watch: {
    show(val) {
      if (val) {
        this.loadCodes()
        this.showCreateForm = false
        this.editingCode = null
        this.resetForm()
      }
    },
    partner() {
      if (this.show) {
        this.loadCodes()
      }
    }
  }
};
</script>

<style scoped>
.charges-row > td {
  background-color: #f8f8f8;
}

.modal {
  display: block !important;
  overflow-y: auto;
}

.modal-dialog {
  overflow-y: initial !important
}

.modal-content {
  max-height: calc(100vh - 3.5rem);
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
